import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { errorCode, errorMessage } from '@/api/errors';
import { LostPasswordScreen } from '@/auth/LostPasswordScreen';
import { plainText } from '@/auth/plainText';
import { ResetPasswordScreen } from '@/auth/ResetPasswordScreen';
import { AlertBox } from '@/common/AlertBox';
import { WBSFullWidthButton } from '@/common/WBSButton';
import { useLogin } from '~/hooks/auth/useLogin';
import {
  AuthCheckbox,
  AuthContainer,
  AuthField,
  AuthHeading,
  AuthHeadingLink,
  AuthHeadingText,
  AuthLabel,
  AuthLink,
  AuthLogoLink,
} from '~/styles/authStyles';
import { formatString } from '~/utils/stringFormat';

interface LoginSearch {
  action?: 'lostpassword' | 'rp';
  key?: string;
  login?: string;
  reset?: 'done';
}

/**
 * The router's default search parser runs each value through `JSON.parse`, so a
 * value that happens to look like a number arrives as one. Reset keys are
 * `token_urlsafe` and all-digits is vanishingly unlikely, but a key silently
 * dropped here would surface as "your link is invalid", which is the one wrong
 * answer this screen can give.
 */
function asText(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

export const Route = createFileRoute('/_auth/login')({
  component: RouteComponent,
  /**
   * `/login/` dispatches on `?action=` because that is legacy's contract:
   * `templates/page-login.php` serves Log In, Lost Password and Reset Password
   * off one URL, and the reset links already in people's inboxes point here.
   */
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    // Core accepts both spellings for the reset screen and its own form posts
    // back `resetpass`, so normalise rather than drop the visitor on Log In.
    const action =
      search.action === 'lostpassword'
        ? 'lostpassword'
        : search.action === 'rp' || search.action === 'resetpass'
          ? 'rp'
          : undefined;

    return {
      action,
      // Only meaningful on the reset screen, which lifts them into state and
      // strips them from the URL on its first render.
      key: action === 'rp' ? asText(search.key) : undefined,
      login: action === 'rp' ? asText(search.login) : undefined,
      reset: search.reset === 'done' ? 'done' : undefined,
    };
  },
});

function RouteComponent() {
  const { login, isPending } = useLogin();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_auth/login' });
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (search.action === 'lostpassword') {
    return <LostPasswordScreen />;
  }

  if (search.action === 'rp') {
    return <ResetPasswordScreen />;
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setError(null);
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get('email') ?? '');
    const password = String(formData.get('password') ?? '');

    login(email, password, rememberMe)
      .then(() => {
        navigate({ to: '/' });
      })
      .catch((failure: unknown) => {
        switch (errorCode(failure)) {
          case 'password_reset_required':
            // The account cannot be used until its password is set. Legacy
            // sends these people to a page of their own; so do we, carrying
            // the address they just typed.
            navigate({
              to: '/first-time-password-reset/',
              search: { email },
            });
            return;
          case 'resource_not_found':
            setError(plainText(t('notifications.error-there-is-no-2')));
            return;
          case 'forbidden':
            setError(
              formatString(
                plainText(t('notifications.error-the-password-you')),
                email,
              ),
            );
            return;
          default:
            setError(
              errorMessage(failure, t('wbs.something-went-wrong-please')),
            );
        }
      });
  };

  return (
    <AuthContainer>
      <AuthLogoLink to="/">
        <img
          alt={t('wbs.world-bible-school')}
          src="https://worldbibleschool.org/wp-content/uploads/2020/06/stack_logo-EN-200w.png"
        />
      </AuthLogoLink>
      <AuthHeading>
        {/* Translation Needed */}
        <AuthHeadingText>Log In</AuthHeadingText>
        <AuthHeadingLink to="/register">
          {t('actions.create-an-account')}
        </AuthHeadingLink>
      </AuthHeading>
      {/* The post-reset notice. Legacy carries this on `?attempt=`, a one-shot
          60-second transient; a search param survives a reload instead, which
          is the tradeoff for not having a server render to hang it on. */}
      {search.reset === 'done' && (
        <AlertBox variant="success">
          {t('wbs.your-password-has-been-reset')}
        </AlertBox>
      )}

      {error !== null && <AlertBox variant="error">{error}</AlertBox>}

      <form onSubmit={handleSubmit}>
        <AuthLabel htmlFor="email">
          {t('notifications.email-address')}
        </AuthLabel>
        <AuthField id="email" name="email" type="email" fullWidth />

        <AuthLabel htmlFor="password">{t('wbs.password')}</AuthLabel>
        <PasswordWrapper>
          <AuthField
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
          />
          {/* Translation Needed */}
          <PasswordToggle
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label="Toggle password visibility"
          >
            {showPassword ? (
              <FontAwesomeIcon icon={faEyeSlash} />
            ) : (
              <FontAwesomeIcon icon={faEye} />
            )}
          </PasswordToggle>
        </PasswordWrapper>

        <RememberMeWrapper>
          <RememberMeLabel htmlFor="rememberme">
            <AuthCheckbox
              id="rememberme"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>{t('profile.remember-me')}</span>
          </RememberMeLabel>
        </RememberMeWrapper>

        {/* Translation Needed */}
        <WBSFullWidthButton type="submit" wide isLoading={isPending}>
          Log In
        </WBSFullWidthButton>
      </form>
      <FooterLinks>
        <AuthLink to="/privacy">{t('wbs.privacy-policy')}</AuthLink>
        <LinkSeparator>|</LinkSeparator>
        <AuthLinkWrapper>
          <Link
            to="/login/"
            search={{
              action: 'lostpassword',
            }}
          >
            {t('wbs.forgot-password')}
          </Link>
        </AuthLinkWrapper>
      </FooterLinks>
    </AuthContainer>
  );
}

const AuthLinkWrapper = styled.span`
  a {
    color: var(--wbs-red);
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
  }
`;

const PasswordWrapper = styled.div`
  margin-bottom: 20px;
  position: relative;
  width: 100%;

  ${AuthField} {
    margin-bottom: 0;
    padding-right: 40px;
    width: 100%;
  }
`;

const PasswordToggle = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: var(--black);
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  &:hover {
    color: var(--grey-800);
  }

  &:focus {
    outline: 2px solid var(--wbs-red);
    outline-offset: 2px;
  }
`;

const RememberMeWrapper = styled.div`
  margin-bottom: 20px;
`;

const RememberMeLabel = styled.label`
  align-items: center;
  color: var(--black);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  line-height: normal;

  @media (max-width: 768px) {
    align-items: flex-start;
    display: flex;
  }
`;

const FooterLinks = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  font-size: 13px;
  gap: 10px;
  justify-content: center;
  margin-top: 18px;
  width: 100%;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 6px 10px;
    justify-content: center;
  }

  > * {
    align-items: center;
    display: inline-flex;
  }
`;

const LinkSeparator = styled.span`
  color: var(--grey-450);
  font-size: 16px;
  font-weight: 300;
`;
